/**
 * The live `ApiClient`: the seam implemented over `@match-point/api-client-ts`,
 * the client generated from `packages/api-contract/openapi.yaml`.
 *
 * This file is the translation boundary and the only one. The wire is
 * snake_case with value-or-null markers; the seam is camelCase with
 * `Reported<T>` and discriminated unions. Keeping every mapping here means a
 * contract change surfaces as compile errors in one file rather than as
 * behavior changes scattered through eighteen screens.
 *
 * Three rules this file holds, deliberately:
 *
 *   - **It drops, never forwards, the financial band** (I-4). `MatchSubscores`
 *     arrives with a `financial` member and the seam's `RankSubscores` has no
 *     slot for it; financial-axis explanations are filtered out entirely rather
 *     than translated. Sender screens cannot render what never crosses this
 *     boundary.
 *   - **It never synthesizes disclosure** (I-3/I-5). The masked/revealed card
 *     union is mapped variant-for-variant from the server's `disclosure`
 *     discriminator, and the decide-conflict path *re-reads* the card rather
 *     than assembling one from local state.
 *   - **It never maps unreported to zero** (I-6). Every nullable wire number
 *     stays `null` or becomes `Reported`; there is no `?? 0` here and there
 *     must never be one.
 */

import {
  createMatchPointClient,
  type MatchPointClient,
  type components
} from '@match-point/api-client-ts'

import {
  ApiError,
  composeHomePayload,
  openBedsAcross,
  reported,
  unreported,
  type AccountDecisionInput,
  type AccountDecisionResult,
  type ApprovalState,
  type CreateProgramInput,
  type AddFormOptionInput,
  type ManagedForm,
  type StaffNotificationPreferences,
  type ManagedQuestionOptions,
  type UpdateFormOptionInput,
  type ProgramDetail,
  type ProgramFormDefinition,
  type ProgramListRow,
  type SaveProgramSectionInput,
  type SaveProgramSectionReceipt,
  type SubmitProgramResult,
  type ProgramSeatsInput,
  type ProgramSeatsReceipt,
  type AccountQueueQuery,
  type ApiClient,
  type HomeFacts,
  type OrgImage,
  type OrgLocation,
  type OrgProgramSummary,
  type OrgImageContentType,
  type OrgImageUpload,
  type DirectoryFilters,
  type OrgPublicContact,
  type OrgView,
  type OwnOrgProfile,
  type Page,
  type PageQuery,
  type PendingAccount,
  type DirectoryQuery,
  type DirectoryResult,
  type DirectoryResults,
  type PendingProfile,
  type PendingProgram,
  type ProfileDecisionInput,
  type ProfileDecisionResult,
  type ProfileEditReceipt,
  type ProfileQueueQuery,
  type ProgramDecisionInput,
  type ProgramDecisionResult,
  type ProgramQueueQuery,
  type Reported,
  type SessionContext,
  type TaxonomyTerm,
  type ProgramHouse,
  type UpdateOrgProfileInput,
  type WeeklySchedule
} from './contract'
import { networkError, toApiError } from './errors'

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

export interface GeneratedAdapterOptions {
  /** API Gateway base URL for the environment. No trailing slash. */
  baseUrl: string
  /** The current Cognito ID token, or `undefined` when signed out. Called per request. */
  getToken: () => Promise<string | undefined>
  context: SessionContext
  /** Override `fetch`, for tests. */
  fetch?: (input: Request) => Promise<Response>
}

// ── Call plumbing ──────────────────────────────────────────────────────────

interface CallResult<T> {
  data?: T
  error?: unknown
  response: Response
}

/** Unwraps an openapi-fetch result, mapping every failure to `ApiError`. */
/** One row of the provider's own program list. */
function toProgramRow (row: {
  id: string
  name: string
  program_type: string
  form_source?: string
  approval_state: string
  version: number
  sections_complete: string[]
  city?: string
  region?: string
  seats: { total: number | null, open: number | null }
  houses: Array<Wire<'ProgramHouseSummary'>>
}): ProgramListRow {
  return {
    id: row.id,
    name: row.name,
    programType: row.program_type,
    formSource: row.form_source ?? null,
    approvalState: row.approval_state as ApprovalState,
    version: row.version,
    sectionsComplete: row.sections_complete,
    city: row.city ?? null,
    region: row.region ?? null,
    seats: { total: row.seats.total, open: row.seats.open },
    houses: row.houses.map(house => ({
      id: house.id,
      streetAddress: house.street_address ?? null,
      city: house.city ?? null,
      region: house.region ?? null,
      seats: { total: house.seats.total, open: house.seats.open }
    }))
  }
}

/** A further house for the wire (ADR 0013). The id goes back when there is one. */
const toWireHouse = (house: ProgramHouse): Wire<'ProgramHouse'> => ({
  ...(house.id !== undefined ? { id: house.id } : {}),
  ...(house.streetAddress !== undefined ? { street_address: house.streetAddress } : {}),
  city: house.city,
  region: house.region,
  ...(house.postalCode !== undefined ? { postal_code: house.postalCode } : {}),
  ...(house.countyRegion !== undefined ? { county_region: house.countyRegion } : {})
})

const fromWireHouse = (house: Wire<'ProgramHouse'>): ProgramHouse => ({
  ...(house.id !== undefined ? { id: house.id } : {}),
  ...(house.street_address !== undefined ? { streetAddress: house.street_address } : {}),
  city: house.city,
  region: house.region,
  ...(house.postal_code !== undefined ? { postalCode: house.postal_code } : {}),
  ...(house.county_region !== undefined ? { countyRegion: house.county_region } : {})
})

async function call<T> (invoke: () => Promise<CallResult<T>>): Promise<T> {
  let result: CallResult<T>
  try {
    result = await invoke()
  } catch (cause) {
    throw networkError(cause)
  }
  if (!result.response.ok) throw toApiError(result.response.status, result.error)
  return result.data as T
}

// ── Small shared mappings ──────────────────────────────────────────────────

/** The wire's `TaxonomyRef` and the seam's `TaxonomyTerm` are the same shape. */
const toTerm = (ref: Wire<'TaxonomyRef'>): TaxonomyTerm => ({
  id: ref.id,
  kind: ref.kind,
  label: ref.label
})

/** A schedule, snake-cased for the wire. Days go out as given; the server orders them. */
const toWireSchedule = (schedule: WeeklySchedule): Wire<'WeeklySchedule'> => ({
  tracks: schedule.tracks.map(track => ({ days: [...track.days], start: track.start, end: track.end })),
  ...(schedule.tracksCombinable !== undefined ? { tracks_combinable: schedule.tracksCombinable } : {})
})

const fromWireSchedule = (schedule: Wire<'WeeklySchedule'>): WeeklySchedule => ({
  tracks: schedule.tracks.map(track => ({ days: track.days, start: track.start, end: track.end })),
  ...(schedule.tracks_combinable !== undefined ? { tracksCombinable: schedule.tracks_combinable } : {})
})

function toPage<W, T> (
  items: readonly W[],
  pagination: Wire<'Pagination'>,
  map: (item: W) => T
): Page<T> {
  return {
    items: items.map(map),
    page: Math.floor(pagination.offset / pagination.limit) + 1,
    pageSize: pagination.limit,
    total: pagination.total
  }
}

/** page/pageSize → the wire's limit/offset. Twenty per page, as every paginated item specifies. */
function toOffsets (query: PageQuery): { limit: number, offset: number } {
  const limit = query.pageSize ?? 20
  const page = Math.max(1, query.page ?? 1)
  return { limit, offset: (page - 1) * limit }
}


function toPublicContact (contact: Wire<'OrgPublicContact'> | undefined): OrgPublicContact | null {
  if (!contact) return null
  return {
    ...(contact.website_url !== undefined ? { websiteUrl: contact.website_url } : {}),
    ...(contact.facebook !== undefined ? { facebook: contact.facebook } : {}),
    ...(contact.instagram !== undefined ? { instagram: contact.instagram } : {})
  }
}

/**
 * The picture, or null when the server omitted it.
 *
 * Omitted rather than null on the wire, because a listing with no logo renders
 * a fallback mark rather than having to tell two kinds of nothing apart. This
 * is the one place that distinction is collapsed, and it is collapsed inwards:
 * the rest of the app sees `null`, which is what the rest of this adapter does
 * with every other optional block.
 */
function toOrgImage (image: Wire<'OrgImage'> | undefined): OrgImage | null {
  return image === undefined ? null : { url: image.url, contentType: image.content_type }
}

function toLocation (location: Wire<'OrgLocation'>): OrgLocation {
  return { city: location.city, region: location.region }
}

function toProgramSummary (summary: Wire<'OrgProgramSummary'>): OrgProgramSummary {
  return {
    programCount: summary.program_count,
    levelsOfCare: summary.levels_of_care.map(toTerm),
    ageRanges: summary.age_ranges.map(range => ({ min: range.min ?? null, max: range.max ?? null })),
    insuranceInNetwork: summary.insurance_in_network.map(toTerm),
    populations: summary.populations.map(toTerm),
    diagnoses: summary.diagnoses.map(toTerm)
  }
}

function toOrgView (view: Wire<'OrgProfileView'>): OrgView {
  if (view.view === 'owner') {
    const owner: OwnOrgProfile = {
      view: 'owner',
      orgId: view.org.id,
      name: view.org.name,
      orgStatus: view.org.status,
      approvalState: view.approval_state,
      version: view.version,
      pendingFields: view.pending_fields,
      description: view.description ?? null,
      highlights: [...view.highlights],
      image: toOrgImage(view.image),
      contact: toPublicContact(view.contact),
      locations: view.locations.map(toLocation),
      programSummary: toProgramSummary(view.program_summary),
      updatedAt: view.updated_at
    }
    return owner
  }
  return {
    view: 'directory',
    orgId: view.org.id,
    name: view.org.name,
    description: view.description ?? null,
    highlights: [...view.highlights],
    image: toOrgImage(view.image),
    contact: toPublicContact(view.contact),
    locations: view.locations.map(toLocation),
    programSummary: toProgramSummary(view.program_summary),
    updatedAt: view.updated_at
  }
}

function toPendingAccount (row: Wire<'AccountForReview'>): PendingAccount {
  return {
    orgId: row.org_id,
    orgName: row.name,
    status: row.status,
    submittedAt: row.submitted_at
  }
}

/**
 * `liveVersion` stays `null` rather than becoming `0` (I-6). "This provider has
 * no approved version" and "version zero" are different claims and only the
 * first is true.
 */
function toPendingProfile (row: Wire<'ProfileForReview'>): PendingProfile {
  return {
    orgId: row.org_id,
    orgName: row.org_name,
    orgStatus: row.org_status,
    version: row.version,
    approvalState: row.approval_state,
    liveVersion: row.live_version ?? null,
    submittedAt: row.submitted_at
  }
}

/**
 * One directory row.
 *
 * Both nullable members are `?? null` rather than left undefined: the contract
 * types `distance_miles` value-or-null and omits `insurance_network` entirely
 * when the search named no insurance, and a screen that had to distinguish
 * `undefined` from `null` would grow a branch for a difference that means
 * nothing to it.
 */
function toDirectoryResult (row: Wire<'DirectoryProgram'>): DirectoryResult {
  return {
    programId: row.program_id,
    name: row.name,
    programType: row.program_type,
    orgId: row.org_id,
    orgName: row.org_name,
    city: row.city ?? null,
    region: row.region ?? null,
    distanceMiles: row.distance_miles ?? null,
    distanceBasis: row.distance_basis ?? null,
    insuranceNetwork: row.insurance_network ?? null,
    ageMin: row.age_min ?? null,
    ageMax: row.age_max ?? null,
    admissionsPhone: row.admissions_phone ?? null,
    orgImageUrl: row.org_image_url ?? null,
    houseCount: row.house_count,
    specialties: row.specialties
  }
}

/**
 * One row of the program review queue.
 *
 * `live_version` is `?? null` for the reason the profile mapper gives: the
 * contract types it value-or-null and the generated type is optional, so both
 * spellings of "never approved" have to arrive as the same one.
 */
function toPendingProgram (row: Wire<'ProgramForReview'>): PendingProgram {
  return {
    programId: row.program_id,
    name: row.name,
    programType: row.program_type,
    orgId: row.org_id,
    orgName: row.org_name,
    orgStatus: row.org_status,
    version: row.version,
    approvalState: row.approval_state,
    liveVersion: row.live_version ?? null,
    city: row.city ?? null,
    region: row.region ?? null,
    submittedAt: row.submitted_at
  }
}

function toManagedQuestion (data: Wire<'ManagedQuestionOptions'>): ManagedQuestionOptions {
  return {
    source: data.source,
    key: data.key,
    question: data.question,
    kinds: data.kinds,
    ordered: data.ordered,
    alsoAskedOn: data.also_asked_on,
    options: data.options.map(option => ({
      taxonomyId: option.taxonomy_id,
      label: option.label,
      canonicalLabel: option.canonical_label,
      kind: option.kind,
      retired: option.retired,
      protectedReason: option.protected ?? null,
      programCount: option.program_count
    }))
  }
}

const idempotency = (key: string): { 'Idempotency-Key': string } => ({ 'Idempotency-Key': key })

// ── The adapter ────────────────────────────────────────────────────────────

export function createGeneratedAdapter (options: GeneratedAdapterOptions): ApiClient {
  const client: MatchPointClient = createMatchPointClient({
    baseUrl: options.baseUrl,
    getToken: options.getToken,
    ...(options.fetch ? { fetch: options.fetch } : {})
  })
  const { context } = options

  /** The caller's own org id, which addresses the owner-only routes. */
  function ownOrgId (): string {
    const orgId = context.currentOrgId()
    if (orgId === null) {
      throw new ApiError('Not signed in.', { status: 401, retryable: false })
    }
    return orgId
  }

  async function getOrg (id: string): Promise<OrgView> {
    const data = await call(() =>
      client.GET('/orgs/{id}', { params: { path: { id } } }))
    return toOrgView(data)
  }

  async function listPendingAccounts (query: AccountQueueQuery): Promise<Page<PendingAccount>> {
    const data = await call(() =>
      client.GET('/admin/accounts', {
        params: {
          query: {
            ...toOffsets(query),
            ...(query.status !== undefined ? { status: query.status } : {})
          }
        }
      }))
    return toPage(data.items, data.pagination, toPendingAccount)
  }

  async function listPendingProfiles (query: ProfileQueueQuery): Promise<Page<PendingProfile>> {
    const data = await call(() =>
      client.GET('/admin/profiles', {
        params: {
          query: {
            ...toOffsets(query),
            ...(query.state !== undefined ? { state: query.state } : {})
          }
        }
      }))
    return toPage(data.items, data.pagination, toPendingProfile)
  }

  async function searchDirectory (query: DirectoryQuery): Promise<DirectoryResults> {
    const location = query.location ?? {}

    const data = await call(() =>
      client.POST('/directory/search', {
        body: {
          ...toOffsets(query),
          ...(query.programTypes?.length ? { program_types: [...query.programTypes] } : {}),
          ...(query.insurance?.length ? { insurance: [...query.insurance] } : {}),
          // Always sent, never omitted: `default: any` in the contract makes the
          // generated type require it. Sending the documented default explicitly
          // is the same request the server would have built anyway.
          insurance_network: query.insuranceNetwork ?? 'any',
          ...(query.populations?.length ? { populations: [...query.populations] } : {}),
          ...(query.diagnoses?.length ? { diagnoses: [...query.diagnoses] } : {}),
          ...(query.admissionCriteria?.length ? { admission_criteria: [...query.admissionCriteria] } : {}),
          ...(query.specialties?.length ? { specialties: [...query.specialties] } : {}),
          // Zero is a real age, so the test is against undefined rather than
          // falsiness — `query.ageMin ? ...` would drop a search for a newborn.
          ...(query.ageMin !== undefined ? { age_min: query.ageMin } : {}),
          ...(query.ageMax !== undefined ? { age_max: query.ageMax } : {}),
          ...(query.levelsOfCare?.length ? { levels_of_care: [...query.levelsOfCare] } : {}),
          ...(query.sessionTimes?.length ? { session_times: [...query.sessionTimes] } : {}),
          ...(query.maxLengthWeeks !== undefined ? { max_length_weeks: query.maxLengthWeeks } : {}),
          ...(query.attendance?.length ? { attendance: [...query.attendance] } : {}),
          ...(query.sort !== undefined ? { sort: query.sort } : {}),
          ...(query.orgId !== undefined ? { org_id: query.orgId } : {}),
          // Omitted rather than sent empty. The server reads an absent axis as
          // "not filtered" and an empty list would be indistinguishable from a
          // filter nobody touched — which is the difference between the whole
          // directory and none of it.
          ...(location.region !== undefined || location.city !== undefined ||
              location.originPostalCode !== undefined || location.radiusMiles !== undefined
            ? {
                location: {
                  ...(location.region !== undefined ? { region: location.region } : {}),
                  ...(location.city !== undefined ? { city: location.city } : {}),
                  ...(location.originPostalCode !== undefined
                    ? { origin_postal_code: location.originPostalCode }
                    : {}),
                  ...(location.radiusMiles !== undefined ? { radius_miles: location.radiusMiles } : {})
                }
              }
            : {})
        }
      }))

    return {
      ...toPage(data.items, data.pagination, toDirectoryResult),
      excludedWithoutLocation: data.excluded_without_location ?? null,
      excludedWithoutAges: data.excluded_without_ages ?? null
    }
  }

  async function listPendingPrograms (query: ProgramQueueQuery): Promise<Page<PendingProgram>> {
    const data = await call(() =>
      client.GET('/admin/programs', {
        params: {
          query: {
            ...toOffsets(query),
            ...(query.state !== undefined ? { state: query.state } : {})
          }
        }
      }))
    return toPage(data.items, data.pagination, toPendingProgram)
  }

  /**
   * Hoisted out of the returned object because `getHome` counts listings and
   * sums their beds, and a method on the literal is not in scope there.
   */
  async function listPrograms (): Promise<readonly ProgramListRow[]> {
    const data = await call(() => client.GET('/programs', {}))
    return data.items.map(toProgramRow)
  }

  async function getOrgProfile (): Promise<OwnOrgProfile> {
    const view = await getOrg(ownOrgId())
    if (view.view !== 'owner') {
      // The server returns the owner view for the caller's own org; anything
      // else here means the session's org id and the token disagree.
      throw new ApiError('This session cannot read its own profile.', { status: 403, retryable: false })
    }
    return view
  }

  return {
    getTaxonomy: async () => {
      const data = await call(() => client.GET('/criteria/taxonomy', {}))
      return data.items.map(toTerm)
    },

    getDirectoryFilters: async (): Promise<DirectoryFilters> => {
      const data = await call(() => client.GET('/directory/filters', {}))
      return {
        programTypes: data.program_types.map(toTerm),
        diagnoses: data.diagnoses.map(toTerm),
        insurance: data.insurance.map(toTerm),
        specialties: data.specialties.map(toTerm),
        levelsOfCare: data.levels_of_care.map(toTerm),
        attendance: data.attendance.map(toTerm),
        byProgramType: data.by_program_type.map(entry => ({
          programType: toTerm(entry.program_type),
          diagnoses: entry.diagnoses.map(toTerm),
          insurance: entry.insurance.map(toTerm),
          specialties: entry.specialties.map(toTerm),
          levelsOfCare: entry.levels_of_care.map(toTerm),
          attendance: entry.attendance.map(toTerm),
          ages: entry.ages,
          sessionTimes: entry.session_times,
          lengthWeeks: entry.length_weeks
        }))
      }
    },




    getOrgProfile,

    getOrg,

    // ── Programs (ADR 0007) ────────────────────────────────────────────────
    //
    // The mapping here is thinner than everywhere else in this file on purpose.
    // The form is data, so a screen renders whatever arrives; translating the
    // questions into app-shaped types would be a second definition of five forms
    // and would need editing every time their authors change one.

    getProgramForm: async (source: string): Promise<ProgramFormDefinition> => {
      const data = await call(() =>
        client.GET('/programs/forms/{source}', { params: { path: { source } } }))

      return {
        source: data.source,
        title: data.title,
        estimate: data.estimate ?? null,
        sections: data.sections.map(section => ({
          key: section.key,
          title: section.title,
          helper: section.helper ?? null,
          questions: section.questions.map(question => ({
            key: question.key,
            question: question.question,
            inputType: question.input_type,
            required: question.required,
            helper: question.helper ?? null,
            options: question.options.map(option => ({
              taxonomyId: option.taxonomy_id,
              label: option.label,
              ...(option.retired === true ? { retired: true as const } : {})
            })),
            ...(question.revealed_by !== undefined
              ? {
                  revealedBy: {
                    questionKey: question.revealed_by.question_key,
                    taxonomyId: question.revealed_by.taxonomy_id
                  }
                }
              : {})
          }))
        }))
      }
    },

    listPrograms,

    createProgram: async (input: CreateProgramInput, idempotencyKey: string): Promise<ProgramListRow> => {
      const data = await call(() =>
        client.POST('/programs', {
          params: { header: idempotency(idempotencyKey) },
          body: {
            form_source: input.formSource,
            name: input.name
          }
        }))

      return {
        id: data.id,
        name: data.name,
        programType: data.program_type,
        formSource: data.form_source,
        approvalState: data.approval_state as ApprovalState,
        version: data.version,
        sectionsComplete: [],
        city: null,
        region: null,
        // A program is created with no counts recorded; the create response
        // does not carry them because there is nothing to carry.
        seats: { total: null, open: null },
        houses: []
      }
    },

    updateProgramSeats: async (
      id: string,
      input: ProgramSeatsInput,
      idempotencyKey: string
    ): Promise<ProgramSeatsReceipt> => {
      const data = await call(() =>
        client.PATCH('/programs/{id}/seats', {
          params: { path: { id }, header: idempotency(idempotencyKey) },
          body: {
            ...(input.total !== undefined ? { total: input.total } : {}),
            ...(input.open !== undefined ? { open: input.open } : {}),
            ...(input.locationId !== undefined ? { location_id: input.locationId } : {})
          }
        }))

      return {
        seats: { total: data.seats.total, open: data.seats.open },
        approvalState: data.approval_state as ApprovalState,
        version: data.version
      }
    },

    getProgram: async (id: string, view?: 'owner' | 'directory'): Promise<ProgramDetail> => {
      const data = await call(() => client.GET('/programs/{id}', {
        params: { path: { id }, ...(view !== undefined ? { query: { view } } : {}) }
      }))

      return {
        view: data.view,
        id: data.id,
        name: data.name,
        programType: data.program_type,
        formSource: data.form_source ?? null,
        orgId: data.org_id,
        location: {
          streetAddress: data.location.street_address ?? null,
          city: data.location.city ?? null,
          region: data.location.region ?? null,
          postalCode: data.location.postal_code ?? null,
          countyRegion: data.location.county_region ?? null
        },
        answers: {
          text: data.answers.text,
          selections: data.answers.selections,
          schedules: Object.fromEntries(
            Object.entries(data.answers.schedules).map(([key, schedule]) => [key, fromWireSchedule(schedule)])),
          locations: Object.fromEntries(
            Object.entries(data.answers.locations).map(([key, houses]) => [key, houses.map(fromWireHouse)]))
        },
        // Absent on a directory read, and kept absent here as null rather than
        // invented, so a screen cannot render a review state that was withheld.
        approvalState: (data.approval_state as ApprovalState | undefined) ?? null,
        version: data.version ?? null,
        sectionsComplete: data.sections_complete ?? null
      }
    },

    saveProgramSection: async (
      id: string,
      input: SaveProgramSectionInput,
      idempotencyKey: string
    ): Promise<SaveProgramSectionReceipt> => {
      // Every mutating request needs this. It was missing here and nothing
      // caught it: the .NET test client attaches a fresh key to every request,
      // so a client that forgets the header looks correct in every endpoint
      // test. The real stack answered 400 the first time the wizard tried to
      // save a step.
      const data = await call(() =>
        client.PATCH('/programs/{id}/profile', {
          params: { path: { id }, header: idempotency(idempotencyKey) },
          body: {
            section: input.section,
            ...(input.text !== undefined ? { text: input.text } : {}),
            ...(input.selections !== undefined
              ? { selections: input.selections as Record<string, string[]> }
              : {}),
            ...(input.schedules !== undefined
              ? {
                  schedules: Object.fromEntries(
                    Object.entries(input.schedules).map(([key, schedule]) => [key, toWireSchedule(schedule)]))
                }
              : {}),
            ...(input.locations !== undefined
              ? {
                  locations: Object.fromEntries(
                    Object.entries(input.locations).map(([key, houses]) => [key, houses.map(toWireHouse)]))
                }
              : {})
          }
        }))

      return {
        approvalState: data.approval_state,
        version: data.version,
        section: data.section,
        sectionsComplete: data.sections_complete,
        missingRequired: data.missing_required
      }
    },

    submitProgram: async (id: string, idempotencyKey: string): Promise<SubmitProgramResult> => {
      const data = await call(() =>
        client.POST('/programs/{id}/profile/submit', {
          params: { path: { id }, header: idempotency(idempotencyKey) }
        }))

      return {
        approvalState: data.approval_state,
        version: data.version,
        missingRequired: data.missing_required
      }
    },

    updateOrgProfile: async (input: UpdateOrgProfileInput, idempotencyKey: string): Promise<ProfileEditReceipt> => {
      const data = await call(() =>
        client.PATCH('/orgs/{id}', {
          params: { path: { id: ownOrgId() }, header: idempotency(idempotencyKey) },
          body: {
            ...(input.description !== undefined ? { description: input.description } : {}),
            ...(input.highlights !== undefined ? { highlights: [...input.highlights] } : {}),
            ...(input.image !== undefined
              ? {
                  image: 'remove' in input.image
                    ? { remove: true }
                    : { media_id: input.image.mediaId }
                }
              : {}),
            ...(input.contact !== undefined
              ? {
                  contact: {
                    ...(input.contact.websiteUrl !== undefined ? { website_url: input.contact.websiteUrl } : {}),
                    ...(input.contact.facebook !== undefined ? { facebook: input.contact.facebook } : {}),
                    ...(input.contact.instagram !== undefined ? { instagram: input.contact.instagram } : {})
                  }
                }
              : {})
          }
        }))
      return {
        approvalState: data.approval_state,
        version: data.version,
        appliedImmediately: data.applied_immediately,
        pendingReview: data.pending_review
      }
    },

    createOrgImageUpload: async (
      contentType: OrgImageContentType,
      idempotencyKey: string
    ): Promise<OrgImageUpload> => {
      const data = await call(() =>
        client.POST('/orgs/{id}/profile-image-uploads', {
          params: { path: { id: ownOrgId() }, header: idempotency(idempotencyKey) },
          body: { content_type: contentType }
        }))
      return {
        mediaId: data.media_id,
        uploadUrl: data.upload_url,
        fields: data.fields,
        maxBytes: data.max_bytes,
        contentType: data.content_type,
        expiresAt: data.expires_at
      }
    },

    uploadOrgImage: async (upload: OrgImageUpload, file: Blob): Promise<void> => {
      // Checked here so the refusal is in the app's words. S3 refuses it too,
      // with a 400 and an XML body, which is the wrong thing to show a provider
      // who just picked a photograph instead of a logo.
      if (file.size > upload.maxBytes) {
        throw new Error(
          `That image is ${Math.ceil(file.size / (1024 * 1024))} MB, and the limit is ` +
          `${Math.floor(upload.maxBytes / (1024 * 1024))} MB. Try a smaller one.`
        )
      }

      // A form, in the order the server gave, with the file last. S3 reads the
      // policy out of these fields and stops at the first one that disagrees
      // with the signature — so they are copied rather than rebuilt, and
      // nothing here decides the key or the content type.
      const form = new FormData()
      for (const [name, value] of Object.entries(upload.fields)) {
        form.append(name, value)
      }
      form.append('file', file)

      // Deliberately `fetch` rather than the generated client. This request
      // does not go to Match Point: it carries no session, no idempotency key
      // and no problem document, and routing it through `call()` would wrap an
      // S3 policy failure in an error shape that claims the product refused it.
      //
      // No `Content-Type` header is set. `FormData` makes the browser write its
      // own multipart boundary, and overriding it produces a body S3 cannot
      // parse. The type the object is stored as travels as a *field*, which is
      // why it is in `fields` rather than in a header.
      // This file *is* the seam the rule below points at, and this one request
      // is the only thing in it that does not go to Match Point. The rule
      // exists so a screen cannot bypass the ApiClient; the screen here calls
      // `uploadOrgImage`, which is exactly what it is meant to do.
      // eslint-disable-next-line no-restricted-globals
      const response = await fetch(upload.uploadUrl, { method: 'POST', body: form })

      if (!response.ok) {
        throw new Error(
          `The image could not be uploaded (${response.status}). ` +
          'The upload link may have expired — try choosing the file again.'
        )
      }
    },

    listPendingAccounts,

    listPendingProfiles,

    decideAccount: async (orgId: string, input: AccountDecisionInput, idempotencyKey: string): Promise<AccountDecisionResult> => {
      const data = await call(() =>
        client.POST('/admin/accounts/{id}/decision', {
          params: { path: { id: orgId }, header: idempotency(idempotencyKey) },
          body: { decision: input.decision, note: input.note }
        }))
      return { orgId: data.org_id, status: data.status, decidedAt: data.decided_at }
    },

    decideOrgProfile: async (orgId: string, input: ProfileDecisionInput, idempotencyKey: string): Promise<ProfileDecisionResult> => {
      const data = await call(() =>
        client.POST('/admin/orgs/{id}/profile-decision', {
          params: { path: { id: orgId }, header: idempotency(idempotencyKey) },
          body: { decision: input.decision, note: input.note }
        }))
      return {
        orgId: data.org_id,
        version: data.version,
        approvalState: data.approval_state,
        decidedAt: data.decided_at
      }
    },

    searchDirectory,

    listPendingPrograms,

    decideProgram: async (programId: string, input: ProgramDecisionInput, idempotencyKey: string): Promise<ProgramDecisionResult> => {
      const data = await call(() =>
        client.POST('/admin/programs/{id}/decision', {
          params: { path: { id: programId }, header: idempotency(idempotencyKey) },
          body: { decision: input.decision, note: input.note }
        }))
      return {
        programId: data.program_id,
        version: data.version,
        approvalState: data.approval_state,
        decidedAt: data.decided_at
      }
    },

    // ── Staff notification preferences (NTF-04, ADR 0017) ───────────────────

    getStaffNotificationPreferences: async (): Promise<StaffNotificationPreferences> => {
      const data = await call(() => client.GET('/admin/me/notification-preferences'))
      return { email: { ...data.email } }
    },

    putStaffNotificationPreferences: async (
      preferences: StaffNotificationPreferences,
      idempotencyKey: string
    ): Promise<StaffNotificationPreferences> => {
      const data = await call(() =>
        client.PUT('/admin/me/notification-preferences', {
          params: { header: idempotency(idempotencyKey) },
          body: { email: { ...preferences.email } }
        }))
      return { email: { ...data.email } }
    },

    // ── Staff option editor (ADM-08, ADR 0015) ──────────────────────────────

    listManagedForms: async (): Promise<readonly ManagedForm[]> => {
      const data = await call(() => client.GET('/admin/forms'))
      return data.forms.map(form => ({
        source: form.source,
        title: form.title,
        questions: form.questions.map(question => ({
          key: question.key,
          question: question.question,
          section: question.section,
          inputType: question.input_type,
          optionCount: question.option_count,
          retiredCount: question.retired_count
        }))
      }))
    },

    getManagedQuestionOptions: async (source: string, key: string): Promise<ManagedQuestionOptions> =>
      toManagedQuestion(await call(() =>
        client.GET('/admin/forms/{source}/questions/{key}/options', { params: { path: { source, key } } }))),

    addFormOption: async (source: string, key: string, input: AddFormOptionInput, idempotencyKey: string): Promise<ManagedQuestionOptions> =>
      toManagedQuestion(await call(() =>
        client.POST('/admin/forms/{source}/questions/{key}/options', {
          params: { path: { source, key }, header: idempotency(idempotencyKey) },
          body: {
            label: input.label,
            ...(input.kind !== undefined ? { kind: input.kind } : {}),
            ...(input.alsoSources !== undefined ? { also_sources: [...input.alsoSources] } : {})
          }
        }))),

    updateFormOption: async (
      source: string,
      key: string,
      taxonomyId: string,
      input: UpdateFormOptionInput,
      idempotencyKey: string
    ): Promise<ManagedQuestionOptions> =>
      toManagedQuestion(await call(() =>
        client.PATCH('/admin/forms/{source}/questions/{key}/options/{id}', {
          params: { path: { source, key, id: taxonomyId }, header: idempotency(idempotencyKey) },
          body: {
            ...(input.label !== undefined ? { label: input.label } : {}),
            ...(input.retired !== undefined ? { retired: input.retired } : {})
          }
        }))),

    reorderFormOptions: async (
      source: string,
      key: string,
      taxonomyIds: readonly string[] | null,
      idempotencyKey: string
    ): Promise<ManagedQuestionOptions> =>
      toManagedQuestion(await call(() =>
        client.PUT('/admin/forms/{source}/questions/{key}/options/order', {
          params: { path: { source, key }, header: idempotency(idempotencyKey) },
          body: { taxonomy_ids: taxonomyIds === null ? null : [...taxonomyIds] }
        }))),

    /**
     * There is no `/home` endpoint — CLI-05 is composed here from the calls
     * that exist, per role, with `allSettled` so one failed panel does not
     * blank the page (DESIGN.md §10's partial state for Home).
     */
    getHome: async () => {
      const role = context.currentRole() ?? 'provider_admin'
      const name = context.currentDisplayName()

      const facts: {
        programCount: Reported<number>
        bedsOpen: Reported<number>
        pendingAccounts: Reported<number>
        pendingProfiles: Reported<number>
        failedPanelTitles: string[]
      } = {
        programCount: unreported(),
        bedsOpen: unreported(),
        pendingAccounts: unreported(),
        pendingProfiles: unreported(),
        failedPanelTitles: []
      }

      if (role === 'provider_admin') {
        // Beds come from the programs, not the organization profile. Programs
        // are what the directory lists and what PRG-11 writes; a count read from
        // the org row would contradict the list one click away.
        const [listings] = await Promise.allSettled([listPrograms()])
        if (listings.status === 'fulfilled') {
          facts.programCount = reported(listings.value.length)
          facts.bedsOpen = openBedsAcross(listings.value)
        } else {
          facts.failedPanelTitles.push('Programs you have listed', 'Beds open')
        }
      } else {
        // Settled independently, so a failure in one queue leaves the other's
        // count on screen rather than blanking both (DESIGN.md §10's partial
        // state for Home). Two queues because an approved org's later profile
        // edits never re-enter the account queue.
        const [queue, profiles] = await Promise.allSettled([
          listPendingAccounts({ page: 1, pageSize: 1 }),
          listPendingProfiles({ page: 1, pageSize: 1 })
        ])
        if (queue.status === 'fulfilled') facts.pendingAccounts = reported(queue.value.total)
        else facts.failedPanelTitles.push('Accounts awaiting approval')
        if (profiles.status === 'fulfilled') facts.pendingProfiles = reported(profiles.value.total)
        else facts.failedPanelTitles.push('Profile changes awaiting review')
      }

      return composeHomePayload(role, name, facts satisfies HomeFacts)
    }
  }
}
